import React, { useCallback, useEffect, useState } from "react";
import styles from './styles.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import TableSkeleton from "../../../../components/Skeleton";

import {
    createCertificateTemplateVersion,
    getAllCertificateTemplateVersions,
    updateCertificateTemplateVersion,
} from "../../../../redux/features/certificateTemplateVersion/certificateTemplateVersionService";
import {
    STATUS,
    resetCertificateTemplateVersionDetails,
    selectCertificateList,
    selectCertificateTemplateVersionList,
    selectUpdateCertificateTemplateVersion,
} from "../../../../redux/features/certificateTemplateVersion/certificateTemplateVersionSlice";
import Modal from "../../../../components/Modal";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { useNavigate, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { pdf, PDFViewer } from "@react-pdf/renderer";
import QRCode from "qrcode";
import IBSUOfficialCertificate from "../../../../components/Certificate/IBSUCertificate/IBSUOfficial";
import IBSCOfficialCertificate from "../../../../components/Certificate/IBSCCertificate/IBSCOfficial";
import IBSCOfficialCertificatePreview from "../../../../components/Certificate/IBSCCertificate/IBSCPreview";
import IBSUOfficialCertificatePreview from "../../../../components/Certificate/IBSUCertificate/IBSUPreview";
import FilterDropdown from "../../../../components/FilterDropdown";

const CertificateTemplateVersion = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
    });
    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setDisabledBtn] = useState(false);
    const [skip, setSkip] = useState(0);
    const [limit] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [initialEntity, setInitialEntity] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
    });

    const [errorMsg, setErrorMsg] = useState(null);
    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);

    const { status, data: certificateTemplateVersions, pageCount } = useSelector(selectCertificateTemplateVersionList);
    const isLoading = status === STATUS.LOADING;

    const { status: updateStatus, message: updateMsg } = useSelector(selectUpdateCertificateTemplateVersion);
    const isUpdating = updateStatus === STATUS.LOADING;

    const fetchAllCertificateTemplateVersions = useCallback(() => {
        dispatch(getAllCertificateTemplateVersions({
            skip,
            limit,
            search: searchQuery,
        }));
    }, [dispatch, skip, limit, searchQuery]);

    useEffect(() => {
        fetchAllCertificateTemplateVersions();
    }, [fetchAllCertificateTemplateVersions]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrorMessages((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = "Name is required.";
        setErrorMessages(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateCertificateTemplateVersion = async () => {
        if (!validateForm()) return;
        setDisabledBtn(true);
        try {
            const data = {
                ...formData,
                certificate_template_id: id
            }
            const response = await dispatch(createCertificateTemplateVersion(data)).unwrap();
            showToast("success", 'Certificate entity created successfully.');
            setFormData({ name: '', code: '' });
            fetchAllCertificateTemplateVersions();
        } catch (error) {
            showToast("error", error.message);
        } finally {
            setDisabledBtn(false);
        }
    };

    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    // Status update
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedEntityId, setSelectedEntityId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(updateCertificateTemplateVersion({ id: selectedEntityId, version_status: 'Active' })).unwrap();
            setSelectedEntityId(null);
            setInitialEntity(null);
            showToast('success', response.message);
            fetchAllCertificateTemplateVersions();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(updateCertificateTemplateVersion({ id: selectedEntityId, status: false })).unwrap();
            setSelectedEntityId(null);
            setInitialEntity(null);
            showToast('success', response.message);
            fetchAllCertificateTemplateVersions();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Edit
    const handleSelectedEntityChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedEntity((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditEntity = () => {
        const errors = {};
        const trimmedName = selectedEntity.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!selectedEntity.code) {
            errors.code = "Code is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialEntity[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editEntityDetails = async () => {
        if (isValidEditEntity(selectedEntity)) {
            const data = getChangedValues(selectedEntity);
            const hasChanges = Object.keys(data).some(key => key !== 'id');
            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(updateCertificateTemplateVersion(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedEntity(null);
                    setInitialEntity(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllCertificateTemplateVersions();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    const [loadingCertificateId, setLoadingCertificateId] = useState(null);

    const openCertificate = async (certificate) => {
        try {
            setLoadingCertificateId(certificate.id);
            const qrDataUrl = await QRCode.toDataURL(
                `${certificate.name}_preview`,
                { width: 100 }
            );
            if (certificate?.certificate_template?.name === 'IBSU Offical Certificate') {
                const pdfBlob = await pdf(<IBSUOfficialCertificatePreview data={[certificate, qrDataUrl]} />).toBlob();
                const pdfUrl = URL.createObjectURL(pdfBlob);
                window.open(pdfUrl, "_blank");
            }
            if (certificate?.certificate_template?.name === 'IBSC Official Certificate') {
                const pdfBlob = await pdf(<IBSCOfficialCertificatePreview data={[certificate, qrDataUrl]} />).toBlob();
                const pdfUrl = URL.createObjectURL(pdfBlob);
                window.open(pdfUrl, "_blank");
            }
        } catch (error) {
            showToast(
                "error",
                "An error occurred while generating the PDF. Please try again."
            );
        } finally {
            setLoadingCertificateId(null);
        }
    }

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add Certificate Template Version</h1>
                <div className={styles.formContainer}>
                    <form className={styles.coordinatorForm} noValidate>
                        {/* Name */}
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>
                                        Version Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name || ""}
                                        onChange={handleInputChange}
                                        placeholder="Enter Version (e.g., v1.0.0)"
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Certificate Phrase 1</label>
                                    <input
                                        type="text"
                                        name="certificate_phrase_1"
                                        value={formData.certificate_phrase_1 || ""}
                                        onChange={handleInputChange}
                                        placeholder="This is to certify that"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Certificate Phrase 2</label>
                                    <input
                                        type="text"
                                        name="certificate_phrase_2"
                                        value={formData.certificate_phrase_2 || ""}
                                        onChange={handleInputChange}
                                        placeholder="is hereby recognized for"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Certificate Phrase 3</label>
                                    <input
                                        type="text"
                                        name="certificate_phrase_3"
                                        value={formData.certificate_phrase_3 || ""}
                                        onChange={handleInputChange}
                                        placeholder="is hereby recognized for"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Conducted By Phrase</label>
                                    <input
                                        type="text"
                                        name="conducted_by_phrase"
                                        value={formData.conducted_by_phrase || ""}
                                        onChange={handleInputChange}
                                        placeholder="Conducted by"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Completion Phrase</label>
                                    <input
                                        type="text"
                                        name="completion_phrase"
                                        value={formData.completion_phrase || ""}
                                        onChange={handleInputChange}
                                        placeholder="Completed on"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Conducted By</label>
                                    <input
                                        type="text"
                                        name="conducted_by"
                                        value={formData.conducted_by || ""}
                                        onChange={handleInputChange}
                                        placeholder="School of Accounting and Finance (SAFSA), IBSUniversity"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div> */}
                        {/* <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Signature Name 1</label>
                                    <input
                                        type="text"
                                        name="signature_name_1"
                                        value={formData.signature_name_1 || ""}
                                        onChange={handleInputChange}
                                        placeholder="Prof. Ramachandran Arunachalam"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div> */}
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Signature Designation 1</label>
                                    <input
                                        type="text"
                                        name="signature_designation_1"
                                        value={formData.signature_designation_1 || ""}
                                        onChange={handleInputChange}
                                        placeholder="Vice Chancellor"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Signature Name 2</label>
                                    <input
                                        type="text"
                                        name="signature_name_2"
                                        value={formData.signature_name_2 || ""}
                                        onChange={handleInputChange}
                                        placeholder="Dr. Arumugam Mourougane"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div> */}
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Signature Designation 2</label>
                                    <input
                                        type="text"
                                        name="signature_designation_2"
                                        value={formData.signature_designation_2 || ""}
                                        onChange={handleInputChange}
                                        placeholder="Head of School - SOAF"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type="button"
                            disabled={disabledBtn}
                            onClick={handleCreateCertificateTemplateVersion}
                        >
                            {disabledBtn ? <BeatLoader color="#ffffff" /> : `Create`}
                        </button>

                    </form>
                </div>
            </div>
            {isLoading ? (
                <div className={styles.versionCardContainer}>
                    {[...Array(4)].map((_, index) => (
                        <div key={index} className={styles.versionCard}>
                            <h3 className={styles.versionTitle}>
                                <Skeleton height={25} width="60%" />
                            </h3>

                            <div className={styles.section}>
                                <Skeleton count={4} height={20} style={{ marginBottom: 8 }} />
                            </div>

                            <div className={styles.section}>
                                <Skeleton count={2} height={20} style={{ marginBottom: 8 }} />
                            </div>

                            <div className={styles.section}>
                                <Skeleton height={20} width="40%" style={{ marginBottom: 10 }} />
                                <div className={styles.signatureRow}>
                                    <div style={{ width: "48%" }}>
                                        <Skeleton count={2} height={15} />
                                    </div>
                                    <div style={{ width: "48%" }}>
                                        <Skeleton count={2} height={15} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : certificateTemplateVersions.length > 0 && (
                <div className="modified-card-grid">
                    {certificateTemplateVersions.map((version) => (
                        <div
                            key={version.id}
                            className={`modified-card ${version?.version_status === "Active" && styles.activeCard
                                }`}
                        >
                            <div className={"modified-card-header"}>
                                <h2 className="modified-card-name">
                                    {version?.name}
                                </h2>
                                {version?.version_status !== 'Active' &&
                                    <button
                                        type="button"
                                        className={styles.activateBtn}
                                        onClick={() => {
                                            setSelectedEntityId(version?.id);
                                            setActivateModalOpen(true);
                                        }}>Make Version Active</button>}
                            </div>
                            <div className={styles.section}>
                                <table className="modified-card-table">
                                    <tbody>
                                        <tr>
                                            <td><strong>Phrase 1:</strong></td>
                                            <td>{version?.certificate_phrase_1}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Phrase 2:</strong></td>
                                            <td>{version?.certificate_phrase_2}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Phrase 3:</strong></td>
                                            <td>{version?.certificate_phrase_3}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Completion:</strong></td>
                                            <td>{version?.completion_phrase}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Conducted By:</strong></td>
                                            <td>{version?.conducted_by_phrase}</td>
                                        </tr>

                                        {/* Section Title Row */}
                                        <tr>
                                            <td colSpan="2" className={styles.sectionTitle}>
                                                <strong>Signatures</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><strong>Signature 1:</strong></td>
                                            <td>{version?.signature_designation_1}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Signature 2:</strong></td>
                                            <td>{version?.signature_designation_2}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="modified-card-actions">
                                <button className="modified-card-action-button action-view"
                                    onClick={() => openCertificate(version)}>
                                    Donwload
                                </button>
                                <button
                                    className="modified-card-action-button action-edit"
                                    onClick={() => {
                                        dispatch(resetCertificateTemplateVersionDetails());
                                        navigate(`/admin/certificate-template/${id}/versions/${version.id}`)
                                    }}
                                >
                                    Edit
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Update Entity Details</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='full_name'
                                                name='full_name'
                                                value={selectedEntity.name || ''}
                                                onChange={handleSelectedEntityChange}
                                                placeholder='Enter StaffMember Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Code<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='code'
                                                name='code'
                                                value={selectedEntity.code || ''}
                                                onChange={handleSelectedEntityChange}
                                                placeholder='Enter Code'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.code && (
                                                <p className={`text-danger`}>{editErrorMsgs.code}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editEntityDetails}
                                    >
                                        {isUpdating ? <BeatLoader color='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Template Version Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the selected version?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Certificate Entity Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the certificate entity?`}
                    colorType={`var(--danger)`}
                />
            )}
            {/* <PDFViewer width={800} height={1400}>
                <IBSCOfficialCertificatePreview data={[certificateTemplateVersions[1], certificateTemplateVersions[1]?.name]} />
            </PDFViewer> */}
        </div>
    );
};

export default CertificateTemplateVersion;